import proschet from 'proschet';

import { Locale } from 'chaika-enums';
import type { TranslationValues } from 'chaika-translation-data';

import { getTranslateByLocale, translate } from '@chaika/i18n/helper';

type ProschetFunction = (index: number | string) => string;
type DeclensionKeys = [TranslationValues, TranslationValues, TranslationValues];

const SINGLE_FORM_LOCALES: Locale[] = [Locale.Ge, Locale.Am];

export class Declension {
    public static yearsIn(locale: Locale): ProschetFunction {
        return getLocalizedDeclension(locale, ['year', 'yearTwo', 'years']);
    }

    public static monthsIn(locale: Locale): ProschetFunction {
        return getLocalizedDeclension(locale, ['month', 'monthTwo', 'months']);
    }

    public static weeksIn(locale: Locale): ProschetFunction {
        return getLocalizedDeclension(locale, ['week', 'weekTwo', 'weeks']);
    }

    public static daysIn(locale: Locale): ProschetFunction {
        return getLocalizedDeclension(locale, ['day', 'daysTwo', 'days']);
    }

    public static get days(): ProschetFunction {
        return proschet([
            translate('day').toLowerCase(),
            translate('daysTwo').toLowerCase(),
            translate('days').toLowerCase(),
        ]);
    }

    public static get daysEng(): ProschetFunction {
        return proschetEng(['day', 'days']);
    }

    public static get years(): ProschetFunction {
        return proschet([
            translate('year').toLowerCase(),
            translate('yearTwo').toLowerCase(),
            translate('years').toLowerCase(),
        ]);
    }

    public static get yearsEng(): ProschetFunction {
        return proschetEng(['year', 'years']);
    }

    public static get yearsFrom(): ProschetFunction {
        return proschet([
            translate('yearTwo').toLowerCase(),
            translate('years').toLowerCase(),
            translate('years').toLowerCase(),
        ]);
    }

    public static get week(): ProschetFunction {
        return proschet([
            translate('week').toLowerCase(),
            translate('weekTwo').toLowerCase(),
            translate('weeks').toLowerCase(),
        ]);
    }

    public static get weekEng(): ProschetFunction {
        return proschetEng(['week', 'weeks']);
    }

    public static get months(): ProschetFunction {
        return proschet([
            translate('month').toLowerCase(),
            translate('monthTwo').toLowerCase(),
            translate('months').toLowerCase(),
        ]);
    }

    public static get monthsEng(): ProschetFunction {
        return proschetEng(['month', 'months']);
    }

    public static get hours(): ProschetFunction {
        return proschet([
            translate('hour').toLowerCase(),
            translate('hourTwo').toLowerCase(),
            translate('hours').toLowerCase(),
        ]);
    }

    public static get minutes(): ProschetFunction {
        return proschet([
            translate('minute').toLowerCase(),
            translate('minuteTwo').toLowerCase(),
            translate('minutes').toLowerCase(),
        ]);
    }

    public static get selected(): ProschetFunction {
        return proschet([
            translate('chosenOne').toLowerCase(),
            translate('chosen').toLowerCase(),
            translate('chosen').toLowerCase(),
        ]);
    }

    public static get selectedFemale(): ProschetFunction {
        return proschet([
            translate('chosenFem').toLowerCase(),
            translate('chosen').toLowerCase(),
            translate('chosen').toLowerCase(),
        ]);
    }

    public static get referrals(): ProschetFunction {
        return proschet([
            translate('referral').toLowerCase(),
            translate('referrals').toLowerCase(),
            translate('referralMany').toLowerCase(),
        ]);
    }

    public static get medicalCases(): ProschetFunction {
        return proschet([
            translate('episode').toLowerCase(),
            translate('episodeTwo').toLowerCase(),
            translate('episodeMany').toLowerCase(),
        ]);
    }

    public static get activeMedicalCases(): ProschetFunction {
        return proschet([
            translate('activeEpisode').toLowerCase(),
            translate('activeEpisodeTwo').toLowerCase(),
            translate('activeEpisodes').toLowerCase(),
        ]);
    }

    public static get active(): ProschetFunction {
        return proschet([
            translate('activeMasculine').toLowerCase(),
            translate('activeMasculineMany').toLowerCase(),
            translate('activeMasculineMany').toLowerCase(),
        ]);
    }

    public static get all(): ProschetFunction {
        return proschet([
            translate('allOne').toLowerCase(),
            translate('all').toLowerCase(),
            translate('all').toLowerCase(),
        ]);
    }

    public static get drugs(): ProschetFunction {
        return proschet([
            translate('drug').toLowerCase(),
            translate('drugTwo').toLowerCase(),
            translate('drugs').toLowerCase(),
        ]);
    }

    public static get services(): ProschetFunction {
        return proschet([
            translate('service').toLowerCase(),
            translate('services').toLowerCase(),
            translate('servicesDone').toLowerCase(),
        ]);
    }

    public static get doctorSubscriptions(): ProschetFunction {
        return proschet([
            translate('subscription').toLowerCase(),
            translate('subscriptions').toLowerCase(),
            translate('subscriptionsMany').toLowerCase(),
        ]);
    }

    public static get results(): ProschetFunction {
        return proschet([
            translate('resultOne').toLowerCase(),
            translate('resultTwo').toLowerCase(),
            translate('results').toLowerCase(),
        ]);
    }

    public static get render(): ProschetFunction {
        return proschet([
            translate('rendered').toLowerCase(),
            translate('renderedNet').toLowerCase(),
            translate('renderedNet').toLowerCase(),
        ]);
    }

    public static get found(): ProschetFunction {
        return proschet([
            translate('foundFem').toLowerCase(),
            translate('found').toLowerCase(),
            translate('found').toLowerCase(),
        ]);
    }

    public static get foundMasc(): ProschetFunction {
        return proschet([
            translate('foundMasc').toLowerCase(),
            translate('found').toLowerCase(),
            translate('found').toLowerCase(),
        ]);
    }

    public static get entries(): ProschetFunction {
        return proschet([translate('entry'), translate('entries'), translate('entriesMany')]);
    }

    public static get rubles(): ProschetFunction {
        return proschet([
            translate('RUB').toLowerCase(),
            translate('rubTwo').toLowerCase(),
            translate('rubs').toLowerCase(),
        ]);
    }

    public static get materials(): ProschetFunction {
        return proschet([
            translate('material').toLowerCase(),
            translate('materialTwo').toLowerCase(),
            translate('materials').toLowerCase(),
        ]);
    }

    public static get tasks(): ProschetFunction {
        return proschet([
            translate('task').toLowerCase(),
            translate('tasks').toLowerCase(),
            translate('tasksTwo').toLowerCase(),
        ]);
    }
}

function getLocalizedDeclension(locale: Locale, keys: DeclensionKeys): ProschetFunction {
    const [oneForm, fewForm, manyForm] = keys.map(key => getTranslateByLocale(locale, key).toLowerCase());

    if (locale === Locale.Ru) {
        return proschet([oneForm, fewForm, manyForm]);
    }

    if (SINGLE_FORM_LOCALES.includes(locale)) {
        return () => manyForm;
    }

    return proschetEng([oneForm, manyForm]);
}

function proschetEng(titles: [string, string]): ProschetFunction {
    return function bindedProschet(amount: number | string): string {
        return titles[amount === 0 || amount === 1 ? 0 : 1];
    };
}
